import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  TriggerCreate,
  TriggerForEditor,
  UseTriggerRes,
} from "@dots-projects/types";
import { TriggerError } from "@dots-projects/utils/exceptions";
import { getSupaClient, getWorkspace } from "../../lib/supabase";
import { PersistKind, PersistResponse } from "./reducer/reducerTypes";
import invariant from "ts-invariant";
import { useNavigate } from "react-router-dom";
import produce from "immer";

type Method = "GET" | "POST" | "PUT";

async function fetchTriggers(path: string, method: Method, payload?: string) {
  const supabase = getSupaClient();
  const session = await supabase.auth.session();
  const response = await fetch(`${process.env["REACT_APP_API_URL"]}${path}`, {
    method,
    headers: { Authorization: `Bearer ${session?.access_token}` },
    body: method === "GET" ? undefined : payload,
  });

  if (response.ok) {
    const responseJson = await response.json();
    return [responseJson.triggers, responseJson] as [
      TriggerForEditor[],
      PersistResponse
    ];
  } else {
    let reason;
    try {
      reason = (await response.json()).reason;
    } catch (e: any) {
      reason = e.message ?? JSON.stringify(e);
    }

    if (typeof reason === "object") {
      throw new TriggerError(reason);
    }

    throw new Error(reason);
  }
}

/**
 * Fetch a draft trigger with the specified id
 *
 * @param liveTriggerId
 * @param draftOrLive
 * @returns UseQueryResult: {data, isLoading, error, ...}
 */
export function useTrigger(
  liveTriggerId: string,
  draftOrLive: "draft" | "live"
) {
  const key = draftOrLive === "draft" ? "trigger-draft" : "trigger";
  const result = useQuery<UseTriggerRes>([key, liveTriggerId], async () => {
    const path =
      draftOrLive === "draft"
        ? `/triggers/${liveTriggerId}/drafts`
        : `/triggers/${liveTriggerId}`;
    // const [triggers] = await fetchTriggers(path, "GET");
    const tiggersRes = await fetchTriggers(path, "GET");
    const [triggers] = tiggersRes;

    if (triggers && triggers.length > 0)
      return {
        trigger: triggers[0],
        validationErrors: tiggersRes[1]["validationErrors"],
      };
    else throw new Error(`Failed to fetch trigger ${liveTriggerId}`);
  });

  return result;
}

/**
 *
 * This hook handles updating/creating triggers. React Query's useMutation is used to support better
 * caching etc. Usage example:
 *
 * function SomeComponent() {
 *   const mutation = useTriggerMutation()
 *   const handleClick = () => {
 *     mutation.mutate(trigger, parentId) // updating
 *     mutation.mutate(trigger) // create new
 *   }
 *   ...
 * }
 *
 * @returns the mutation which we'll call `mutation.mutate` later on
 */
export function useTriggerMutation() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const mutation = useMutation<
    [TriggerForEditor, PersistResponse],
    Error,
    { kind: PersistKind; trigger: TriggerCreate; parentId?: string }
  >(async ({ kind, trigger, parentId }) => {
    let path,
      method: Method = "POST";
    switch (kind) {
      case "save-draft":
        path = parentId ? `/triggers?parent=${parentId}` : `/triggers`;
        break;
      case "publish":
        {
          const draftId = parentId;
          invariant(draftId, `Publish: draftId is empty.`);
          path = `/triggers/${draftId}/publish`;
        }
        break;
      case "view-draft":
        {
          const liveTriggerId = parentId;
          invariant(liveTriggerId, `Edit: liveTriggerId is empty.`);
          // Strictly speaking getting drafts isn't mutation, but due to the way the backend is implemented, we put the code here for convenience.
          path = `/triggers/${liveTriggerId}/drafts`;
          method = "GET";
        }
        break;
      case "try-draft":
        {
          const draftId = parentId;
          invariant(draftId, `try-draft: draftId is empty.`);
          path = `/triggers/${draftId}/try`;
        }
        break;
      default:
        throw new Error(`Unsupported persist kind ${kind}`);
    }

    // This if a hack to avoid ` Cannot perform 'get' on a proxy that has been revoked` error, which is caused by immer.
    // Apparently the error is caused because we sare creating a non-unidirectional data flow  sometimes within the trigger, which is not allowed by immer.
    // https://github.com/immerjs/immer/issues/936
    const cleanTrigger = produce(trigger, (draft) => {
      draft;
    });
    const [triggers, response] = await fetchTriggers(
      path,
      method,
      JSON.stringify(cleanTrigger)
    );
    if (triggers && triggers.length > 0) {
      const t = triggers[0];
      ////TODO how to invalidate the cache? The main challenge here is what query key we should use.
      // queryClient.setQueryData(["trigger", updatedTrigger.id], updatedTrigger);
      if (kind === "publish") {
        // invalidate trigger list query cache since we need to display the newly published trigger there
        const guildId = localStorage.getItem("guild-id") || "";
        const supabase = getSupaClient();
        const workspace = getWorkspace(supabase);
        queryClient.invalidateQueries([
          "discord-triggers",
          { guildId, workspace },
        ]);
        navigate(`/automation/${t.id}/view`);
      }

      if (kind === "view-draft") {
        navigate(`/automation/${cleanTrigger.id}/edit`);
      }

      return [t, response];
    } else
      throw new Error(`Failed to persist trigger ${parentId}, kind=${kind}`);
  });
  return mutation;
}

export async function persistTrigger(trigger: TriggerCreate) {
  return await fetchTriggers(`/triggers`, "POST", JSON.stringify(trigger));
}
